import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, CalendarDays, MapPin, Users, Wallet, Package, Loader2, Printer, Receipt } from "lucide-react";
import Layout from "@/components/Layout";
import Chat from "@/components/Chat";
import { QuoteCard, QuoteBuilder, StatusBadge } from "@/components/Quote";
import { Invoice } from "@/pages/BookingDetail";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";

// Shared by customer (/enquiries/:id) and vendor (/vendor/leads/:id)
export default function EnquiryDetail({ vendorView = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [data, setData] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [printQuoteId, setPrintQuoteId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => api.get(`/enquiries/${id}`).then((r) => setData(r.data)).finally(() => setLoading(false)), [id]);
  useEffect(() => { if (!user) { setAuthOpen(true); setLoading(false); return; } load(); }, [user, load, setAuthOpen]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return <Layout><div className="py-20 text-center text-muted-foreground">Enquiry not found or login required.</div></Layout>;

  const e = data.enquiry; const role = data.viewer_role;
  const activeQuote = data.quotes.find((q) => q.status === "sent");
  const Wrapper = vendorView ? ({ children }) => <div className="min-h-screen bg-muted/30">{children}</div> : Layout;

  const printQuotation = (q) => {
    setPrintQuoteId(q.id);

    const cleanup = () => {
      setPrintQuoteId(null);
      document.body.classList.remove("quote-printing");
      window.removeEventListener("afterprint", cleanup);
    };

    window.addEventListener("afterprint", cleanup);
    setTimeout(() => {
      document.body.classList.add("quote-printing");
      window.print();
      setTimeout(cleanup, 60000);
    }, 100);
  };

  return (
    <>
      <style>{`
        .quote-print-sheet {
          display: none;
        }

        @media print {
          body.quote-printing * {
            visibility: hidden !important;
          }

          body.quote-printing .quote-print-target,
          body.quote-printing .quote-print-target * {
            visibility: visible !important;
          }

          body.quote-printing .quote-print-target {
            display: block !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 210mm !important;
            min-height: 297mm !important;
            padding: 14mm !important;
            box-sizing: border-box !important;
            background: #ffffff !important;
            color: #172033 !important;
          }

          body.quote-printing .quote-print-target .quote-screen-actions {
            display: none !important;
          }

          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>

      <Wrapper>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate(vendorView ? "/vendor" : "/enquiries")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ChevronLeft className="h-4 w-4" /> Back</button>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <h1 className="font-display font-extrabold text-2xl">{vendorView ? e.customer_name : e.vendor_name}</h1>
            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" /> {e.event_type} · {e.event_date || "TBD"} {e.event_time || ""}</span>
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {e.location}</span>
              {e.guests && <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {e.guests} guests</span>}
              {e.budget && <span className="flex items-center gap-1"><Wallet className="h-4 w-4" /> {formatINR(e.budget)}</span>}
              {e.package && <span className="flex items-center gap-1"><Package className="h-4 w-4" /> {e.package.name} · {formatINR(e.package.price)}</span>}
            </div>
          </div>
          <StatusBadge status={e.status} />
        </div>

        <div className="grid lg:grid-cols-[1fr_400px] gap-6">
          <div className="space-y-4">
            {vendorView && e.customer_phone && <div className="rounded-2xl border border-border bg-card p-4 text-sm"><b>Customer:</b> {e.customer_name} · +91 {e.customer_phone}{e.message && <p className="mt-2 text-muted-foreground">"{e.message}"</p>}</div>}
            <Chat enquiryId={e.id} meRole={role} />
          </div>
          <div className="space-y-4">
            {data.booking && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 p-4" data-testid="enquiry-booking-card">
                <div className="flex justify-between items-center"><b>Booking {data.booking.code}</b><StatusBadge status={data.booking.status} /></div>
                <div className="text-sm mt-1">Total {formatINR(data.booking.total)} · Paid {formatINR(data.booking.paid_amount)}</div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button data-testid="view-booking-btn" size="sm" className="rounded-xl" onClick={() => navigate(vendorView ? "/vendor/bookings" : `/bookings/${data.booking.id}`)}>View booking</Button>
                  {vendorView && (
                    <Button
                      data-testid="vendor-invoice-btn"
                      size="sm"
                      variant="outline"
                      className="rounded-xl"
                      onClick={() => setInvoiceOpen(true)}
                    >
                      <Receipt className="h-4 w-4 mr-1" />
                      Invoice / Receipt
                    </Button>
                  )}
                </div>
              </div>
            )}
            {role === "vendor" && !data.booking && !activeQuote && <QuoteBuilder enquiry={e} onCreated={load} />}
            {role === "vendor" && activeQuote && <p className="text-xs text-muted-foreground px-1">Quote pending customer response. Sending a new quote supersedes it.</p>}
            {role === "vendor" && activeQuote && !data.booking && <details><summary className="text-sm text-purple-600 cursor-pointer px-1">Send revised quote</summary><div className="mt-3"><QuoteBuilder enquiry={e} onCreated={load} /></div></details>}
            {data.quotes.map((q) => (
              <div key={q.id} className="space-y-2">
                {role === "vendor" && (
                  <div className="flex justify-end">
                    <Button
                      data-testid={`quote-print-${q.id}`}
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => printQuotation(q)}
                    >
                      <Printer className="h-4 w-4 mr-1" />
                      Print / Save Quotation
                    </Button>
                  </div>
                )}

                <QuoteCard
                  quote={q}
                  canAct={role === "customer"}
                  onAction={(r) => {
                    load();
                    if (r?.booking) navigate(`/bookings/${r.booking.id}`);
                  }}
                />

                {role === "vendor" && printQuoteId === q.id && (
                  <div className="quote-print-sheet quote-print-target">
                    <div className="border-b-2 border-purple-600 pb-5 flex justify-between items-start">
                      <div>
                        <div className="text-2xl font-extrabold text-slate-900">
                          MakeMyEventPro
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Professional Event Marketplace
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-bold tracking-[0.18em] text-purple-600 uppercase">
                          Quotation
                        </div>
                        <div className="text-lg font-extrabold mt-1">
                          {q.quote_number || "Quotation"}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {q.created_at
                            ? new Date(q.created_at).toLocaleDateString("en-IN")
                            : new Date().toLocaleDateString("en-IN")}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-5 mt-6">
                      <div className="rounded-xl bg-slate-50 border p-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-purple-600 mb-2">
                          Customer
                        </div>
                        <div className="font-bold text-sm">
                          {e.customer_name || "Customer"}
                        </div>
                        {e.customer_phone && (
                          <div className="text-xs text-slate-500 mt-1">
                            +91 {e.customer_phone}
                          </div>
                        )}
                      </div>

                      <div className="rounded-xl bg-slate-50 border p-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-purple-600 mb-2">
                          Event
                        </div>
                        <div className="font-bold text-sm">
                          {e.event_type || "Event"}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {e.event_date || "Date TBD"}
                          {e.event_time ? ` · ${e.event_time}` : ""}
                        </div>
                        <div className="text-xs text-slate-500">
                          {e.location || "Location TBD"}
                          {e.guests ? ` · ${e.guests} guests` : ""}
                        </div>
                      </div>
                    </div>

                    <div className="mt-7">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                            <th className="text-left p-3">Description</th>
                            <th className="text-right p-3">Qty</th>
                            <th className="text-right p-3">Rate</th>
                            <th className="text-right p-3">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(q.items || []).map((item, index) => (
                            <tr key={index} className="border-b border-slate-200">
                              <td className="p-3 text-sm">{item.name}</td>
                              <td className="p-3 text-right text-sm">{item.qty}</td>
                              <td className="p-3 text-right text-sm">{formatINR(item.unit_price)}</td>
                              <td className="p-3 text-right text-sm font-semibold">
                                {formatINR(item.qty * item.unit_price)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex justify-end mt-6">
                      <div className="w-[85mm] space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Subtotal</span>
                          <span>{formatINR(q.subtotal || 0)}</span>
                        </div>
                        {q.discount > 0 && (
                          <div className="flex justify-between">
                            <span>Discount</span>
                            <span>-{formatINR(q.discount)}</span>
                          </div>
                        )}
                        {q.tax > 0 && (
                          <div className="flex justify-between">
                            <span>Tax / GST</span>
                            <span>{formatINR(q.tax)}</span>
                          </div>
                        )}
                        <div className="flex justify-between bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl px-4 py-3 font-extrabold text-lg">
                          <span>Total</span>
                          <span>{formatINR(q.total || 0)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-slate-600">
                          <span>Advance</span>
                          <span>{formatINR(q.advance_amount || 0)}</span>
                        </div>
                        <div className="flex justify-between font-bold">
                          <span>Balance</span>
                          <span>{formatINR(q.balance_amount || 0)}</span>
                        </div>
                      </div>
                    </div>

                    {(q.terms || q.notes || q.cancellation_policy) && (
                      <div className="mt-7 rounded-xl bg-slate-50 border p-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-purple-600 mb-2">
                          Terms & Conditions
                        </div>
                        {q.terms && <p className="text-xs text-slate-600">{q.terms}</p>}
                        {q.notes && <p className="text-xs text-slate-600 mt-2">{q.notes}</p>}
                        {q.cancellation_policy && (
                          <p className="text-xs text-slate-600 mt-2">
                            <b>Cancellation:</b> {q.cancellation_policy}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="mt-12 pt-5 border-t text-center">
                      <div className="font-bold text-slate-800">
                        Thank you for choosing {q.vendor_name || e.vendor_name || "our services"}.
                      </div>
                      <div className="text-xs text-purple-600 font-semibold mt-1">
                        Thank you for choosing MakeMyEventPro.com
                      </div>
                      <div className="text-[9px] text-slate-500 mt-4">
                        MakeMyEventPro.com
                      </div>
                      <div className="text-[9px] font-bold tracking-wider uppercase mt-1">
                        Powered by EXACT MEDIA
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {data.quotes.length === 0 && role === "customer" && <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Waiting for the vendor's quotation.</div>}
          </div>
        </div>
      </div>

      {vendorView && data.booking && (
        <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
          <DialogContent className="bg-white dark:bg-slate-900 rounded-3xl max-w-4xl w-[96vw] max-h-[92vh] overflow-hidden p-0 flex flex-col">
            <DialogHeader className="px-6 pt-5 pb-3 bg-white dark:bg-slate-900 border-b shrink-0">
              <DialogTitle>Invoice / Receipt</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 pb-8 overscroll-contain">
              <Invoice bookingId={data.booking.id} />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Wrapper>
    </>
  );
}
